// H3 数据核对（TDesign）：多条件搜索 + 后端分页列表 + 勾选批量审核/导出/撤回 + 进群数据导出
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Space, Input, Select, DatePicker, Pagination, Dialog, Textarea, MessagePlugin,
  Tag, Table,
} from 'tdesign-react';
import { parttimeApi } from '../../api/parttime';
import { getPageSize } from '../../api/parttimePager';
import ParttimeDataDetailDialog from './ParttimeDataDetailDialog';

// 分页每页条数配置 key（后端全局配置，前端不写死）
const PAGE_SIZE_KEY = 'page_size_H3';

// 处理状态（全量 9 个处理状态；本页仅展示 已发送未领取 及之后状态的数据，未分配/已分配未发送/分配后撤回/分配后拒绝在数据分配页查看）
const STATUS_OPTS = [
  { label: '全部', value: 'ALL' },
  { label: '未分配', value: 'UNASSIGNED' },
  { label: '已分配未发送', value: 'PRE_ASSIGNED' },
  { label: '已发送未领取', value: 'SENT' },
  { label: '已领取进行中', value: 'CLAIMED' },
  { label: '已截图待审核', value: 'SUBMITTED' },
  { label: '截图审核通过', value: 'PASSED' },
  { label: '截图审核不通过', value: 'FAILED' },
  { label: '分配后撤回', value: 'RECALLED' },
  { label: '分配后拒绝', value: 'REJECTED' },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTS.filter(o => o.value !== 'ALL').map(o => [o.value, o.label]));

// 对账状态
const SETTLE_OPTS = [
  { label: '全部', value: 'ALL' },
  { label: '未对账', value: 'UNREVIEWED' },
  { label: '对账中', value: 'SETTLEMENTING' },
  { label: '对账通过', value: 'CONFIRMED' },
  { label: '对账未通过', value: 'DISAGREED' },
];
const SETTLE_LABEL = { UNREVIEWED: '未对账', SETTLEMENTING: '对账中', CONFIRMED: '对账通过', DISAGREED: '对账未通过' };

// 状态标签主题（处理/对账统一配色：通过-绿 / 未通过-红 / 已领取-蓝 / 其余-橙）
const statusTheme = (s) => {
  if (s === 'PASSED' || s === 'PASS' || s === 'CONFIRMED') return 'success';
  if (s === 'FAILED' || s === 'FAIL' || s === 'REJECTED' || s === 'DISAGREED') return 'danger';
  if (s === 'CLAIMED') return 'primary';
  return 'warning';
};

// Tag 组件统一引用（TDesign 胶囊样式，不加自定义样式；空值留空）
const StatusTag = ({ v, label }) => (v ? <Tag theme={statusTheme(v)} variant="light">{label || v}</Tag> : null);

const emptyDate = () => '';
const emptyFilters = () => ({
  ownerName: '', phone: '', wechat: '', community: '', city: '', assignName: '',
  status: 'ALL', settleStatus: 'ALL',
  groupStart: emptyDate(), groupEnd: emptyDate(), settleStart: emptyDate(), settleEnd: emptyDate(),
});

/**
 * H3 数据核对页面
 * 分页从后端配置（page_size_H3）读取，多条件搜索由后端分页接口 /review/check 完成。
 */
export default function ParttimeGroupData() {
  const [filters, setFilters] = useState(emptyFilters());   // 输入区实时值
  const [q, setQ] = useState(emptyFilters());               // 点击"查询"后应用的条件
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sel, setSel] = useState(() => new Set());
  // 查询强制刷新标记：保证无搜索条件时点击「查询」也能触发接口（避免 setState 相同引用被 React 跳过）
  const [reloadKey, setReloadKey] = useState(0);

  // 审核弹窗：approve(通过) / reject(不通过，需原因)
  const [dlg, setDlg] = useState(null); // { approve, dataIds }
  const [reason, setReason] = useState('');
  // 请求中标记：通过/拒绝/导出/撤回 按钮统一 loading，防止重复提交
  const [operating, setOperating] = useState(false);

  const [viewRow, setViewRow] = useState(null);  // 查看详情（H3 查看按钮）

  // 通用装载分页配置（后端读取每页条数）
  useEffect(() => { getPageSize(PAGE_SIZE_KEY).then(setPageSize).catch(() => {}); }, []);

  // 后端分页查询
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: pageSize };
      ['ownerName', 'phone', 'wechat', 'community', 'city', 'assignName'].forEach(k => {
        if (q[k] && String(q[k]).trim()) params[k] = String(q[k]).trim();
      });
      if (q.status && q.status !== 'ALL') params.status = q.status;
      if (q.settleStatus && q.settleStatus !== 'ALL') params.settleStatus = q.settleStatus;
      if (q.groupStart) params.groupStart = q.groupStart;
      if (q.groupEnd) params.groupEnd = q.groupEnd + ' 23:59';      // 结束日含整天
      if (q.settleStart) params.settleStart = q.settleStart;
      if (q.settleEnd) params.settleEnd = q.settleEnd + ' 23:59';
      const res = await parttimeApi.checkList(params);
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) { MessagePlugin.error(e.message); } finally { setLoading(false); }
  }, [q, page, pageSize, reloadKey]);

  useEffect(() => { load(); }, [load]);

  // ---- 勾选（TDesign Table 多选，selectedRowKeys 驱动） ----
  const selectedIds = () => [...sel];

  // ---- TDesign Table 列定义（空值留空） ----
  const columns = [
    { colKey: 'row-select', type: 'multiple', width: 48 },
    { colKey: 'ownerName', title: '业主姓名', ellipsis: true, cell: ({ row }) => row.ownerName || '' },
    { colKey: 'phone', title: '手机号', ellipsis: true, cell: ({ row }) => row.phone || '' },
    { colKey: 'wechat', title: '微信号', ellipsis: true, cell: ({ row }) => row.wechat || '' },
    { colKey: 'city', title: '城市', ellipsis: true, cell: ({ row }) => row.city || '' },
    { colKey: 'community', title: '小区', ellipsis: true, cell: ({ row }) => row.community || '' },
    { colKey: 'assignName', title: '兼职人员', width: 110, cell: ({ row }) => (row.assignName ? <Tag theme="primary" variant="light">{row.assignName}</Tag> : '') },
    { colKey: 'addTime', title: '添加截图', cell: ({ row }) => (row.addCount ? (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>x{row.addCount}</div>
        <div style={{ fontSize: 11, color: '#999' }}>{fmtDt(row.addTime)}</div>
      </div>
    ) : null) },
    { colKey: 'groupTime', title: '进群截图', cell: ({ row }) => (row.groupCount ? (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>x{row.groupCount}</div>
        <div style={{ fontSize: 11, color: '#999' }}>{fmtDt(row.groupTime)}</div>
      </div>
    ) : null) },
    { colKey: 'status', title: '处理状态', width: 110, cell: ({ row }) => <StatusTag v={row.status} label={STATUS_LABEL[row.status]} /> },
    { colKey: 'settleStatus', title: '对账状态', width: 100, cell: ({ row }) => <StatusTag v={row.settleStatus} label={SETTLE_LABEL[row.settleStatus]} /> },
    { colKey: 'rejectReason', title: '兼职拒绝原因', width: 140, ellipsis: true, cell: ({ row }) => row.rejectReason || '' },
    { colKey: 'adminDisagreeReason', title: '客服不同意原因', width: 140, ellipsis: true, cell: ({ row }) => row.adminDisagreeReason || '' },
    { colKey: 'op', title: '操作', width: 180, cell: ({ row }) => rowOperate(row) },
  ];

  // ---- 分页（支持每页条数切换，切换时回第一页） ----
  const onPageChange = (info) => {
    const size = info && info.pageSize;
    const cur = typeof info === 'number' ? info : (info && info.current ? info.current : page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setPage(1);
    } else if (cur) {
      setPage(cur);
    }
  };

  // ---- 批量审核 ----
  const openApprove = (dataIds) => {
    if (!dataIds.length) { MessagePlugin.warning('请先勾选数据'); return; }
    setOperating(true);
    parttimeApi.auditData(dataIds, true, '').then(() => {
      MessagePlugin.success('已审核通过');
      setSel(new Set());
      load();
    }).catch(e => MessagePlugin.error(e.message)).finally(() => setOperating(false));
  };
  const openReject = (dataIds) => {
    if (!dataIds.length) { MessagePlugin.warning('请先勾选数据'); return; }
    setReason('');
    setDlg({ approve: false, dataIds });
  };
  const confirmReject = () => {
    if (!String(reason).trim()) { MessagePlugin.error('请输入不通过原因'); return; }
    setOperating(true);
    parttimeApi.auditData(dlg.dataIds, false, reason.trim()).then(() => {
      MessagePlugin.success('已标记不通过');
      setDlg(null);
      setSel(new Set());
      load();
    }).catch(e => MessagePlugin.error(e.message)).finally(() => setOperating(false));
  };

  // ---- 导出勾选数据（实时生成 Excel→COS，页内下载，不新开标签页）----
  const doExport = async () => {
    if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; }
    setOperating(true);
    try {
      const r = await parttimeApi.exportByDataIds(selectedIds());
      // 优先以 blob 形式触发下载，避免跳转到新页面
      fetch(r.url)
        .then(res => res.blob())
        .then(blob => {
          const obj = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = obj;
          a.download = '导出数据.xlsx';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(obj);
        })
        .catch(() => window.open(r.url, '_blank'));
      MessagePlugin.success(`已导出 ${r.count} 条，生成新 Excel`);
    } catch (e) { MessagePlugin.error(e.message); } finally { setOperating(false); }
  };

  // ---- 撤回数据（回池） ----
  const doWithdraw = async () => {
    if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; }
    setOperating(true);
    try {
      await parttimeApi.withdrawDatas(selectedIds());
      MessagePlugin.success('已撤回');
      setSel(new Set());
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setOperating(false); }
  };

  // ---- 单条操作 ----
  const rowOperate = (r) => {
    return (
      <Space size={4}>
        {r.status === 'SUBMITTED' && (
          <>
            <Button size="small" theme="success" variant="text" loading={operating} disabled={operating} onClick={() => openApprove([r.dataId])}>通过</Button>
            <Button size="small" theme="danger" variant="text" loading={operating} disabled={operating} onClick={() => openReject([r.dataId])}>不通过</Button>
          </>
        )}
        <Button size="small" theme="default" variant="text" onClick={() => openView(r.dataId)}>查看</Button>
      </Space>
    );
  };

  // ---- 查看：完整信息 + 图片预览 + 分配历史/审批时间线 ----
  const openView = async (dataId) => {
    try {
      setViewRow(await parttimeApi.dataDetailAdmin(dataId));
    } catch (e) { MessagePlugin.error(e.message); }
  };

  // 简化日期显示
  const fmtDt = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '');

  return (
    <div className="pt-app">
      {/* 整体页面容器：搜索 + 批量操作 + 列表 为一个整体 */}
      <div className="pt-card" style={{ padding: 20 }}>
        {/* 搜索区（后端分页条件） */}
        <div style={{ paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
          <p className="pt-search-title">数据核对</p>
          <div className="pt-search-row">
            <span className="pt-search-label">业主姓名</span>
            <Input placeholder="业主张三" value={filters.ownerName} onChange={(v) => setFilters(f => ({ ...f, ownerName: v }))} style={{ width: 140 }} clearable />
            <span className="pt-search-label">业主手机号</span>
            <Input placeholder="138****" value={filters.phone} onChange={(v) => setFilters(f => ({ ...f, phone: v }))} style={{ width: 140 }} clearable />
            <span className="pt-search-label">业主微信</span>
            <Input placeholder="微信号" value={filters.wechat} onChange={(v) => setFilters(f => ({ ...f, wechat: v }))} style={{ width: 140 }} clearable />
            <span className="pt-search-label">小区</span>
            <Input placeholder="关键词" value={filters.community} onChange={(v) => setFilters(f => ({ ...f, community: v }))} style={{ width: 140 }} clearable />
            <span className="pt-search-label">城市</span>
            <Input placeholder="城市" value={filters.city} onChange={(v) => setFilters(f => ({ ...f, city: v }))} style={{ width: 120 }} clearable />
            <span className="pt-search-label">兼职</span>
            <Input placeholder="姓名/手机号" value={filters.assignName} onChange={(v) => setFilters(f => ({ ...f, assignName: v }))} style={{ width: 150 }} clearable />
            <span className="pt-search-label">处理状态</span>
            <Select value={filters.status} options={STATUS_OPTS} onChange={(v) => setFilters(f => ({ ...f, status: v }))} style={{ width: 140 }} />
            <span className="pt-search-label">对账状态</span>
            <Select value={filters.settleStatus} options={SETTLE_OPTS} onChange={(v) => setFilters(f => ({ ...f, settleStatus: v }))} style={{ width: 130 }} />
            <span className="pt-search-label">进群时间</span>
            <DatePicker format="YYYY-MM-DD" value={filters.groupStart} onChange={(v) => setFilters(f => ({ ...f, groupStart: v || '' }))} placeholder="开始" style={{ width: 140 }} />
            <DatePicker format="YYYY-MM-DD" value={filters.groupEnd} onChange={(v) => setFilters(f => ({ ...f, groupEnd: v || '' }))} placeholder="结束" style={{ width: 140 }} />
            <span className="pt-search-label">对账时间</span>
            <DatePicker format="YYYY-MM-DD" value={filters.settleStart} onChange={(v) => setFilters(f => ({ ...f, settleStart: v || '' }))} placeholder="开始" style={{ width: 140 }} />
            <DatePicker format="YYYY-MM-DD" value={filters.settleEnd} onChange={(v) => setFilters(f => ({ ...f, settleEnd: v || '' }))} placeholder="结束" style={{ width: 140 }} />
            <Button theme="primary" variant="text" size="small" loading={loading} onClick={() => { setPage(1); setQ(filters); setReloadKey(k => k + 1); }}>查询</Button>
            <Button theme="default" variant="text" size="small" onClick={() => {
              setFilters(emptyFilters());
              setQ(emptyFilters());
              setPage(1);
            }}>重置</Button>
          </div>
        </div>

        {/* 批量操作区（未勾选时点击提示；按钮外观始终一致） */}
        <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
          <Space size={12} wrap>
            <Button size="small" theme="success" variant="text" loading={operating} disabled={operating} onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } openApprove(selectedIds()); }}>批量通过</Button>
            <Button size="small" theme="danger" variant="text" loading={operating} disabled={operating} onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } openReject(selectedIds()); }}>批量不通过</Button>
            <Button size="small" theme="primary" variant="text" loading={operating} disabled={operating} onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } doExport(); }}>导出Excel</Button>
            <Button size="small" theme="warning" variant="text" loading={operating} disabled={operating} onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } doWithdraw(); }}>撤回数据</Button>
            <span style={{ color: '#888' }}>已勾选 {sel.size} 条</span>
          </Space>
        </div>

        {/* 列表 */}
        <div style={{ paddingTop: 14 }}>
          <Table
            rowKey="dataId"
            resizable
            data={rows}
            loading={loading}
            hover
            size="small"
            columns={columns}
            selectedRowKeys={[...sel]}
            onSelectChange={(keys) => setSel(new Set(keys))}
            empty={<span>暂无数据</span>}
            pagination={{
              current: page, pageSize, total,
              onChange: onPageChange,
              totalContent: (t) => `共 ${t} 条数据`,
            }}
          />
        </div>
      </div>

      {/* 批量不通过：原因弹窗 */}
      <Dialog
        header="不通过原因"
        visible={!!dlg}
        footer={null}
        width="380px"
        closeOnOverlayClick
        onClose={() => setDlg(null)}
      >
        <Textarea style={{ width: '100%' }} placeholder="请输入不通过原因（必填，可同步到多条）" value={reason} onChange={(v) => setReason(v)} />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button theme="default" variant="text" size="small" onClick={() => setDlg(null)}>取消</Button>
            <Button theme="danger" variant="text" size="small" loading={operating} disabled={operating} onClick={confirmReject}>确定</Button>
          </Space>
        </div>
      </Dialog>

      {/* 数据详情弹窗（复用共享组件：客户数据 / 兼职截图 / 时间线） */}
      <ParttimeDataDetailDialog row={viewRow} onClose={() => setViewRow(null)} />
    </div>
  );
}